/*
 * dock.js -- spodni CTA lista (<=767 px) a plovouci dock (>=768 px),
 * plan 07-04. Mechanika je prevzata z artboardoveho CHROME_JS
 * (generatory/gen_mobil.py, radky 1394-1432) se tremi zamernymi zmenami:
 *
 *   1. IntersectionObserver sleduje VSECHNY `.formsec` na strance, ne jen
 *      prvni (POKYNY-IMPLEMENTACE bod a: "dock se schovava nad KAZDYM
 *      formsec i patickou"). Artboard bral `querySelector`, takze na
 *      strance se dvema formulari zustal dock nad tim druhym.
 *   2. Detekce klavesnice se dopocitava DVAKRAT (60 a 350 ms po focusin).
 *      WebKit `resize` na visualViewport pri otevreni iOS klavesnice casto
 *      vubec nevystreli (bug 198347), takze na iOS rozhoduje prave ten
 *      timeout -- a 60 ms je pod dobou animace klavesnice (~250-350 ms).
 *   3. Vedle `resize` se posloucha i `scroll` na visualViewport: na iOS se
 *      pri klavesnici meni `offsetTop` a `scroll` prijde spolehlivěji.
 *
 * CO TENHLE MODUL NEDELA
 * Nesahá na `document.body.classList` -- stavy prepina vyhradne
 * `setState()` ze state.js, protoze jsou to VEREJNY KONTRAKT pro externi
 * GTM chatbot widget (docs/chatbot-position-contract.md). Nepouziva
 * `innerHTML` (threat T-07-04-02); jedina zmena DOM mimo stavy je atribut
 * `hidden` na samotne liste a datova znacka na pozorovanych prvcich.
 *
 * PROC SE `hidden` PREPINA SPOLU SE STAVEM
 * Lista je v HTML vzdy pritomna a `hidden` ji drzi mimo layout I mimo
 * pristupnostni strom. Kdyby o viditelnosti rozhodovalo jen CSS, cetla by
 * ctecka odkazy listy i ve chvili, kdy je neviditelna. Atribut a stav
 * `dock-hidden` se proto meni v jedinem miste (`apply()`), nikdy jedno bez
 * druheho.
 *
 * DEGRADACE (kontrakt B)
 * Bez `IntersectionObserver` zustavaji `st.form` i `st.foot` na `false`:
 * lista se po prekroceni prahu ukaze v plne sirce a uz se nezmensi ani
 * neschova. To je zamer -- degradace "vse viditelne", ne prazdna
 * obrazovka. Bez `visualViewport` rozhoduje o klavesnici samotny
 * `focusin` (CONTEXT.md).
 */

import { setState, hasState, contracts } from './state.js';
import * as viewport from './viewport.js';

/** Id sdileneho prvku listy/docku (footer.php). */
import { guard } from './resize-guard.js';

const DOCK_ID = 'th-dock';

/** Podil vysky viewportu, po jehoz odrolovani se lista objevi (spec §2). */
const SCROLL_RATIO = 0.7;

/** Prah IntersectionObserveru -- doslova z artboardu. */
const IO_THRESHOLD = 0.08;

/**
 * Podil referencni vysky, pod kterym se viewport povazuje za zmenseny
 * klavesnici. Vyssi hodnota by chytala i vysunuti adresniho radku.
 */
const KBD_RATIO = 0.8;

/**
 * Kdy se po `focusin` premeruje vyska viewportu. Dve hodnoty, ne jedna:
 * 60 ms chyta rychlou Android klavesnici, 350 ms iOS, kde je animace delsi
 * a `resize` casto nedorazi vubec (WebKit bug 198347).
 */
const KBD_RECHECK_MS = [60, 350];

/**
 * Datova znacka viditelnosti na pozorovanych prvcich.
 *
 * PROC ZNACKA A NE JEDINY BOOLEAN: `st.form` musi zustat `true`, dokud je
 * videt ALESPON JEDEN `.formsec`. IntersectionObserver hlasi zmeny po
 * jednotlivych prvcich, takze bez pameti stavu ostatnich prvku by prvni
 * formular, ktery odjede z obrazovky, shodil `dock-mini` i v okamziku, kdy
 * je druhy porad videt.
 */
const VISIBLE_FLAG = 'thDockVisible';

/*
 * SELEKTOR CHATBOT FABU -- CITOVANY Z KODU WIDGETU, NE ODHADNUTY.
 *
 * /Users/jirka/Development/chatbot-widgets/widgets/widget-terrahunt.js:
 *   radek 110: el(tag, cls) sklada tridu jako 'zw-' + cls
 *   radek 612: var fab = el('div', 'fab')  ->  <div class="zw-fab">
 *   radek 643: .zw-fab{position:fixed;bottom:20px;left:20px;60x60;z-index:99999}
 *   radek 691: @media(max-width:440px){ .zw-fab{bottom:12px;left:12px;52x52} }
 *
 * FAB nema `id`, takze trida je jediny stabilni zachyt. Widget je LIVE a
 * tenhle repozitar ho NEMENI -- lista se mu proto prizpusobuje sama
 * (docs/chatbot-position-contract.md, sekce "Znama kolize").
 */
const FAB_SELECTOR = '.zw-fab';

/**
 * Kdy se bez `MutationObserver` naposledy podivat, jestli uz FAB v DOM je.
 *
 * Widget se vklada az po `fetch` konfigurace (widget-terrahunt.js:697),
 * takze pri `DOMContentLoaded` ani pri `load` jeste byt nemusi. 3 s je
 * horni odhad podle chovani GTM na produkci.
 */
const FAB_FALLBACK_MS = 3000;

/**
 * Horni hranice, do ktere se kolize FABu s listou vubec resi (07-07, bod 5).
 *
 * 767 px, ne 440 jako do 07-06: spodni LISTA existuje az do 767 px a widget
 * ma nad 440 px FAB na `left:20px; bottom:20px` a 60 x 60 -- tedy porad na
 * te sami strane a porad v pasmu, kde lista je. Puvodni hranice 440 px byla
 * hranice jedne media query widgetu, ne hranice problemu.
 */
const FAB_MAX_WIDTH = 767;

/** Selektor tlacitka listy, se kterym FAB fyzicky kolidoval. */
const DOCK_TEL_SELECTOR = '.mdock-tel';

/** Stav kolize (verejny kontrakt pro widget). */
const FAB_STATE = 'chatbot-fab-left';

/**
 * Stavy, ktere MENI GEOMETRII listy (07-07 dodatek 2).
 *
 * Jen jejich zmena (nebo zmena atributu `hidden`) muze posunout boxy, ktere
 * porovnava `updateFabCollision()`. `kbd-active` v seznamu byt nemusi:
 * invariant ze state.js z nej odvozuje `dock-hidden`, takze se zmena
 * projevi na nem.
 */
const DOCK_VISUAL_STATES = ['dock-full', 'dock-mini', 'dock-hidden'];

let dock = null;
let forms = [];
let footers = [];
/** Boční panely s CTA (contracts.SIDE_PANEL); viz `sideOverlapsDock()`. */
let sides = [];
let focused = false;

/** Nalezeny FAB widgetu. Element, ne boolean -- kolize se MERI (07-07). */
let fabEl = null;

/** Je naplanovany rAF s prepisem stavu? (07-07, bod 12) */
let rafScheduled = false;

/**
 * Ma se v pristim snimku premerit kolize FABu, i kdyz se stav listy nezmenil?
 *
 * Nastavuje se tam, kde se geometrie mohla zmenit mimo stavy listy: pri
 * zmene viewportu a pri vlozeni FABu do DOM. Diky tomu jde i tahle mereni
 * pres tutez rAF agregaci jako scroll (WR-02 z code review) -- jeden
 * vynuceny reflow na snimek, ne jeden na udalost.
 */
let fabDirty = false;

const st = { scrolled: false, form: false, foot: false, kbd: false, side: false };

/*
 * ZÓNA DOCKU NAD SPODNÍM OKRAJEM (desktop). Dock sedí vpravo dole a má s
 * mezerami ~200 px; dokud do téhle zóny zasahuje spodek bočního panelu
 * s vlastním CTA (`.dside`/`.coop-dside`, sticky), dock se neukáže, jinak
 * leží dvě CTA přes sebe (majitel 8. 10. 2026, snímek ze stagingu).
 * Měří se obdélník, ne IntersectionObserver: panel je sticky a jeho
 * průnik s viewportem je skoro pořád 100 %, rozhoduje jen poloha spodku.
 */
const SIDE_CLEAR_PX = 220;
const SIDE_MIN_WIDTH = 768;

/*
 * STATICKY DOCK NA DOTYKU (1.27.13, majitel na stagingu 213936: "vraceni
 * CTA zas vratilo i 2x tap" -- dock je spoustec sam o sobe i s pulzem
 * jednou a guardem). Na zarizeni bez mysi (`(hover: hover) and (pointer:
 * fine)` nesedi) dock po prekroceni prahu scrollu JEDNOU vyjede a dal uz
 * NEMENI STAV: zadne hide/show podle paticky/formulare (IO), zadne mereni
 * klavesnice pres `visualViewport` (jeho `resize` chodi i pri navratu
 * listy adresy), zadny puls, zadna reakce na `resize`/`orientationchange`.
 * Jedina dalsi zmena je skryti pri fokusu do pole (`focusin`, uzivatelova
 * akce mimo okno tapu) a navrat po `focusout`. Kazda jina mutace docku
 * (`hidden`, `dock-*` trida, `is-pulsing`) muze padnout do okna, ve
 * kterem WebKit po prvnim tapu po scrollu vyhodnocuje hover fazi, a klik
 * zahodi. Desktop s mysi si necha puvodni chovani (tam zadny tap neni).
 */
const STATIC_TOUCH = !(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);

/** Staticky rezim: dock uz jednou vyjel a stav je zamceny (viz STATIC_TOUCH). */
let staticShown = false;

/** Posledni znama sirka viewportu (1.27.13, `onViewportChange()` height-only guard). */
let dockLastWidth = window.innerWidth;

/**
 * Cas posledniho `resize`, milisekundy (1.27.13, `onIntersect()` guard nize
 * -- objeveno regresnim testem `webkit-tap-gate.mjs --special resizemut`
 * PRI OVEROVANI teto opravy: `observeSections()` sleduje viditelnost
 * `.formsec`/paticky (`IO_THRESHOLD = 0.08`) -- zmena VYSKY viewportu
 * (iOS lista adresy) muze u stranky, kde je paticka blizko okraje,
 * prekrocit tenhle prah stejne jako `pulse.js` puvodne delal u docku:
 * `st.foot` se prepne, `apply()` zmeni `on` a s nim `hidden`/`dock-*`
 * tridu PRAVE v hover fazi tapu. Stejny mechanismus, jiny spoustec --
 * proto stejna 300ms pojistka jako v `pulse.js`.
 */
let dockLastResizeAt = 0;

if (typeof window !== 'undefined') {
	window.addEventListener(
		'resize',
		function () {
			dockLastResizeAt = window.performance && window.performance.now ? window.performance.now() : Date.now();
		},
		{ passive: true }
	);
}

/**
 * Zmenila se od posledniho volani skutecna SIRKA viewportu?
 *
 * @return {boolean}
 */
function dockWidthChanged() {
	const now = window.innerWidth;
	const changed = now !== dockLastWidth;
	dockLastWidth = now;
	return changed;
}

/**
 * Byl uz jednou naplanovan sledovac prijezdu docku
 * (`transitionend`/`animationend`/rAF fallback, viz `armDockPulse()`)?
 * 1.27.13, majitel: puls smi nastoupit AZ PO dokonceni prijezdu docku,
 * jednou za stranku -- ne pres IO (`pulse.js` dock prvky vylucuje, viz
 * jeho docblok u `DOCK_SELECTOR`).
 */
let dockPulseArmed = false;

/** Selektor editovatelnych prvku pro detekci klavesnice (07-07, bod 11). */
const EDITABLE = 'input,textarea,select,[contenteditable]';

/**
 * Je prvek oznaceny jako prave viditelny?
 *
 * @param {HTMLElement} el Pozorovany prvek.
 * @return {boolean}
 */
function isMarkedVisible(el) {
	return '1' === el.dataset[VISIBLE_FLAG];
}

/**
 * Prepise stav na <body> a atribut `hidden` podle aktualnich priznaku.
 *
 * ZAPISUJE SE JEN TO, CO SE MENI (07-07, bod 12). Do 07-06 se pri kazde
 * scroll udalosti volalo `setState()` ctyrikrat bez ohledu na to, jestli se
 * neco zmenilo -- a protoze externi chatbot widget sleduje atribut `class`
 * na <body> `MutationObserver`em, znamenal kazdy takovy zapis dalsi kolo
 * jeho prepoctu. Porovnava se proti SKUTECNYM tridam na <body>, ne proti
 * zapamatovanemu snimku: stav muze zmenit i `enforceInvariant()` ve state.js
 * (napr. kdyz se otevre menu), a snimek by pak lhal a `apply()` by uz nikdy
 * spravny stav neobnovila.
 *
 * @return {void}
 */
function sideOverlapsDock() {
	if (!sides.length || window.innerWidth < SIDE_MIN_WIDTH) {
		return false;
	}
	const limit = window.innerHeight - SIDE_CLEAR_PX;
	return sides.some(function (el) {
		const r = el.getBoundingClientRect();
		return r.height > 0 && r.top < window.innerHeight && r.bottom > limit;
	});
}

function apply() {
	st.side = sideOverlapsDock();
	const on = st.scrolled && !st.foot && !st.kbd && !st.side;

	const desired = {
		'dock-full': on && !st.form,
		'dock-mini': on && st.form,
		'dock-hidden': !on,
		'kbd-active': st.kbd,
	};

	let geometryChanged = false;

	const names = Object.keys(desired);
	for (let i = 0; i < names.length; i += 1) {
		if (hasState(names[i]) !== desired[names[i]]) {
			setState(names[i], desired[names[i]]);
			if (DOCK_VISUAL_STATES.indexOf(names[i]) !== -1) {
				geometryChanged = true;
			}
		}
	}

	if (!dock) {
		return;
	}

	const wasHidden = dock.hasAttribute('hidden');
	if (on) {
		if (wasHidden) {
			dock.removeAttribute('hidden');
		}
	} else if (!wasHidden) {
		dock.setAttribute('hidden', '');
	}
	if (wasHidden !== dock.hasAttribute('hidden')) {
		geometryChanged = true;
	}

	/*
	 * PULS AZ PO PRIJEZDU DOCKU, JEDNOU ZA STRANKU (1.27.13, majitel:
	 * "sjedu niz, vyjede CTA a 2 tapy"). `wasHidden && !hasAttribute` = dock
	 * PRAVE TEREZ prestal byt skryty -- prvni takova chvile na strance
	 * naplanuje sledovac dokonceni prijezdu (`armDockPulse()`), ktery klasu
	 * `is-pulsing` prida az PO nem, ne behem. Dalsi prechody hidden -> visible
	 * (uzivatel scrolluje nahoru pod prah a zase dolu) uz sledovac
	 * neplanuji -- `dockPulseArmed` drzi "uz jednou nastartovano".
	 */
	if (wasHidden && !dock.hasAttribute('hidden')) {
		if (STATIC_TOUCH) {
			// Staticky rezim: prvni vyjeti je zaroven posledni zmena stavu
			// (krome fokusu do pole). Puls JEDNOU po dojezdu (staging 8,
			// majitel chce pulz CTA i na mobilu) -- pres resize-guard, bez IO.
			staticShown = true;
			if (!dockPulseArmed) {
				dockPulseArmed = true;
				armDockPulse();
			}
		} else if (!dockPulseArmed) {
			dockPulseArmed = true;
			armDockPulse();
		}
	}

	/*
	 * KOLIZE SE PREMERUJE JEN PRI ZMENE GEOMETRIE (07-07 dodatek 2).
	 *
	 * Do te doby se `updateFabCollision()` volala z kazdeho rAF snimku, tedy
	 * i pri scrollu, ktery se stavem listy nehnul -- a protoze funkce pred
	 * merenim prepina tridu a hned cte `getBoundingClientRect()`, byl to
	 * vynuceny reflow na kazdy snimek. ZMERENO: 120 volani
	 * `getBoundingClientRect` za 60 scroll snimku (2 na snimek).
	 *
	 * Merit casteji nema co prinest: FAB i lista jsou `position: fixed`,
	 * takze jejich boxy scroll NEMENI. Zmenit je muze jen prechod mezi
	 * stavy listy (full/mini/hidden vc. atributu `hidden`), zmena viewportu
	 * (resize/orientationchange -- vlastni posluchace) nebo vlozeni FABu
	 * (`checkFab()`).
	 */
	if (geometryChanged || fabDirty) {
		fabDirty = false;
		updateFabCollision();
	}
}

/**
 * Prida `.is-pulsing` na pulzujici prvky docku (`.th-pulse`/`.th-pulse--glow`
 * uvnitr `#th-dock`), JEDNOU, potom co dokoncil PRIJEZD (viz `armDockPulse()`).
 *
 * Zamerne NEODEBIRA tridu zpet -- animace `th-pulse-scale`/`th-pulse-glow`
 * (components.css) ma `animation-iteration-count: var(--pulse-count)`
 * (konecne cislo, ne `infinite`), takze sama dobehne a zastavi se. `pulse.js`
 * tridu odebira jen proto, aby ji mohl pripojit ZNOVU pro dalsi opakovani
 * (BD, periodicky puls) -- dock pulzuje jen jednou za stranku, takze druhe
 * pripojeni nikdy nenastane a odebrani by bylo zbytecna dalsi mutace navic.
 *
 * @param {Element[]} targets Prvky uvnitr docku s tridou `.th-pulse[--glow]`.
 * @return {void}
 */
function pulseDockOnce(targets) {
	// Pres sdileny resize-guard (1.27.13, staging 8): trida se prida az mimo
	// okno po zmene vysky viewportu, jinak by mohla padnout do hover faze tapu.
	guard(function () {
		targets.forEach(function (el) {
			el.classList.add('is-pulsing');
		});
	});
}

/**
 * Naplanuje jednorazovy puls docku AZ PO dokonceni jeho prijezdu (1.27.13,
 * majitel: "Když tap přijde během příjezdu docku, nesmí se v tu chvíli nic
 * dalšího měnit").
 *
 * `.mdock` dnes NEMA zadny `transition`/`animation` -- viditelnost resi
 * cisty atribut `hidden` (`display: none` <-> `display: flex`, okamzite,
 * bez prechodu). `transitionend`/`animationend` proto NIKDY nepriji.
 * Posluchace se presto vesi (budoucnost: kdyby nekdo pozdeji pridal
 * skutecny slide-in/fade-in na `.mdock`, prevezme rizeni automaticky,
 * `finish()` je idempotentni) -- ale primarni cesta dnes je rAF fallback:
 * DVA `requestAnimationFrame` za sebou pockaji, az prohlizec dokonci
 * layout/paint po odebrani `hidden` (jeden snimek na layout, jeden na
 * paint), a teprve POTOM pulzni. To je jedina realna "chvile prijezdu",
 * kterou dnesni (bezanimacni) dock ma -- kratsi nez lidska reakcni doba
 * na dalsi tap o rad velikosti, takze prakticky vzdy dobehne drive, nez
 * uzivatel stihne cokoli tapnout.
 *
 * `prefers-reduced-motion`: respektuje stejnou volbu jako `pulse.js` --
 * kdyz uzivatel pohyb odmitl, dock nepulzuje vubec (zadna `.is-pulsing`).
 *
 * @return {void}
 */
function armDockPulse() {
	if (!dock) {
		return;
	}

	if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return;
	}

	const targets = Array.prototype.slice.call(dock.querySelectorAll('.th-pulse, .th-pulse--glow'));
	if (!targets.length) {
		return;
	}

	let done = false;

	function finish() {
		if (done) {
			return;
		}
		done = true;
		dock.removeEventListener('transitionend', onEnd);
		dock.removeEventListener('animationend', onEnd);
		pulseDockOnce(targets);
	}

	function onEnd(event) {
		// Jen prechod/animace NA SAMOTNEM docku, ne probublavajici udalost
		// z potomka (napr. budouci hover efekt na `.mdock-tel`).
		if (event.target !== dock) {
			return;
		}
		finish();
	}

	dock.addEventListener('transitionend', onEnd);
	dock.addEventListener('animationend', onEnd);

	requestAnimationFrame(function () {
		requestAnimationFrame(finish);
	});
}

/**
 * Naplanuje jediny prepis stavu na pristi snimek.
 *
 * PROC rAF A NE PRIME VOLANI: scroll udalost prijde na 60Hz displeji az
 * 100x za sekundu a `apply()` cte layout (`getBoundingClientRect` v detekci
 * kolize FABu) i zapisuje tridy. Bez agregace by vznikal layout thrashing
 * a widget by dostal desitky notifikaci mezi dvema snimky.
 *
 * @return {void}
 */
function schedule() {
	if (rafScheduled) {
		return;
	}
	if (!window.requestAnimationFrame) {
		// Degradace (kontrakt B): bez rAF se zapisuje rovnou.
		apply();
		return;
	}
	rafScheduled = true;
	window.requestAnimationFrame(function () {
		rafScheduled = false;
		apply();
	});
}

/**
 * Prah scrollu. Vola se i pri initu, aby stav odpovidal stránce nactene
 * uprostred (kotva, obnoveni pozice pri navratu zpet).
 *
 * @return {void}
 */
function onScroll() {
	if (STATIC_TOUCH && staticShown) {
		return;
	}
	st.scrolled = window.scrollY > window.innerHeight * SCROLL_RATIO;
	schedule();
}

/**
 * Reakce na zmeny pruniku pozorovanych prvku.
 *
 * IGNOROVANI DO 300 MS PO RESIZE (1.27.13, viz `dockLastResizeAt` docblok
 * vys): zmena vysky viewportu muze prekrocit `IO_THRESHOLD` u paticky/
 * formulare, ktere jsou blizko okraje, bez jakekoli skutecne zmeny
 * scrollu. Zaznam se cely zahodi -- `st.form`/`st.foot` zustanou na
 * predchozi hodnote, dokud se viewport neustali.
 *
 * @param {IntersectionObserverEntry[]} entries Zmenene prvky.
 * @return {void}
 */
function onIntersect(entries) {
	const now = window.performance && window.performance.now ? window.performance.now() : Date.now();
	if (dockLastResizeAt && now - dockLastResizeAt < 300) {
		return;
	}

	entries.forEach(function (entry) {
		entry.target.dataset[VISIBLE_FLAG] = entry.isIntersecting ? '1' : '0';
	});

	st.form = forms.some(isMarkedVisible);
	st.foot = footers.some(isMarkedVisible);

	schedule();
}

/**
 * Rozjede sledovani formularovych sekci a paticky.
 *
 * Selektory jdou z `contracts` ve state.js, ne natvrdo: v F0.5 zadna
 * formularova komponenta neexistuje (testuje se na fixture stránce) a F1a
 * ji napoji zmenou jedineho radku na jedinem miste.
 *
 * @return {void}
 */
function observeSections() {
	if (!window.IntersectionObserver) {
		return;
	}

	forms = Array.prototype.slice.call(document.querySelectorAll(contracts.FORM_SECTION));
	footers = Array.prototype.slice.call(document.querySelectorAll(contracts.FOOTER));

	const targets = forms.concat(footers);
	if (!targets.length) {
		return;
	}

	const io = new window.IntersectionObserver(onIntersect, { threshold: IO_THRESHOLD });
	targets.forEach(function (el) {
		el.dataset[VISIBLE_FLAG] = '0';
		io.observe(el);
	});
}

/**
 * Premeri, jestli je otevrena virtualni klavesnice.
 *
 * Bez `visualViewport` rozhoduje samotny fokus v poli -- merit neni cim
 * a schovat listu pri psani je porad spravnejsi nez ji nechat prekryvat
 * pole (CONTEXT.md: "bez visualViewport = jen focusin").
 *
 * @return {void}
 */
function measureKeyboard() {
	if (!window.visualViewport || STATIC_TOUCH) {
		// Staticky rezim na dotyku: rozhoduje jen fokus v poli, ne vyska
		// visualViewportu (ta se meni i pri navratu listy adresy po tapu).
		st.kbd = focused;
		schedule();
		return;
	}

	st.kbd = focused && viewport.height() < viewport.getBase() * KBD_RATIO;
	schedule();
}

/**
 * Fokus do formularoveho pole: klavesnice se teprve otevira, takze se
 * vyska meri az po chvili (dvakrat, viz KBD_RECHECK_MS).
 *
 * @param {FocusEvent} event Udalost.
 * @return {void}
 */
function onFocusIn(event) {
	const target = event.target;
	if (!target || !target.matches || !target.matches(EDITABLE)) {
		return;
	}

	focused = true;
	KBD_RECHECK_MS.forEach(function (ms) {
		window.setTimeout(measureKeyboard, ms);
	});
}

/**
 * Odchod z pole -- ale az po overeni, KAM fokus odesel (07-07, bod 11).
 *
 * Do 07-06 se priznak shazoval okamzite. Jenze prechod mezi dvema poli
 * formulare je dvojice udalosti `focusout` (z prvniho) + `focusin` (do
 * druheho) V TEMZE KOLE SMYCKY: klavesnice se pritom vubec nezavre, ale
 * lista na okamzik naskocila pres rozepsany formular a zase zmizela.
 *
 * `setTimeout(..., 0)` posune kontrolu za konec aktualniho tasku, tedy do
 * okamziku, kdy uz `document.activeElement` ukazuje na CIL, ne na `body`.
 * Kdyz je cilem zase editovatelny prvek, nedela se nic. Cekani je jedno kolo
 * smycky, ne pevna pauza -- lista se tedy po skutecnem odklepnuti vrati
 * stejne rychle jako driv.
 *
 * @return {void}
 */
function onFocusOut() {
	window.setTimeout(function () {
		const active = document.activeElement;
		if (active && active.matches && active.matches(EDITABLE)) {
			return;
		}
		focused = false;
		st.kbd = false;
		schedule();
	}, 0);
}

/**
 * Protinaji se dva obdelniky?
 *
 * Tataz logika jako v `verification/_07-fab-collision.cjs` -- merici nastroj
 * a merene chovani musi pouzivat tentyz vzorec, jinak by gate mohl svitit
 * zelene na jineho definici pruniku, nez ma sablona.
 *
 * @param {DOMRect|null} a Prvni obdelnik.
 * @param {DOMRect|null} b Druhy obdelnik.
 * @return {boolean}
 */
function intersects(a, b) {
	if (!a || !b || !a.width || !a.height || !b.width || !b.height) {
		return false;
	}

	return !(a.right <= b.left || b.right <= a.left || a.bottom <= b.top || b.bottom <= a.top);
}

/**
 * ZMERI kolizi FABu s tel: tlacitkem listy a podle vysledku prepne stav.
 *
 * PROC GEOMETRIE A NE EXISTENCE PRVKU (07-07, bod 5): do 07-06 stacilo, ze
 * `.zw-fab` v DOM je -- trida `chatbot-fab-left` se nasadila i tehdy, kdyz
 * se widget presune doprava (sekce 5 kontraktu) a zadny prekryv by nevznikl.
 * Lista by pak uhybala necemu, co ji nikde nestoji v ceste.
 *
 * PROC SE PRED MERENIM STAV DOCASNE VYPINA: pravidla pod `chatbot-fab-left`
 * telefon PRESUNOU doprava, takze v uhnutem stavu se boxy uz neprotinaji.
 * Naivni mereni by tedy tridu hned zase sundalo, telefon by skocil zpatky,
 * dalsi mereni by ji nasadilo -- blikani pri kazdem scrollu. Meri se proto
 * ZAKLADNI geometrie (stav vypnuty) a teprve vysledek se zapise. Cely
 * postup bezi v jednom tasku, takze mezi vypnutim a zapnutim neni paint --
 * uzivatel nic neuvidi.
 *
 * KDY SE TO VOLA (07-07 dodatek 2): jen kdyz se opravdu mohla zmenit
 * geometrie -- pri prechodu mezi stavy listy (`apply()` si to hlida sam),
 * pri `resize`/`orientationchange` a pri vlozeni FABu. NE z kazdeho rAF
 * snimku: cteni `getBoundingClientRect()` hned po zapisu tridy je vynuceny
 * reflow, a FAB i lista jsou `position: fixed`, takze jejich boxy se
 * scrollem nehybou. Puvodni bezpodminecne volani stalo 120 volani
 * `getBoundingClientRect` za 60 scroll snimku; po zmene 0, dokud se stav
 * listy nezmeni.
 *
 * PASMO: do 767 px, tedy tam, kde existuje spodni LISTA. Nad tim je dock
 * plovouci vpravo a FAB widgetu vlevo -- k prekryvu nema jak dojit.
 *
 * @return {void}
 */
function updateFabCollision() {
	if (!fabEl || !dock) {
		return;
	}

	if (window.innerWidth > FAB_MAX_WIDTH || dock.hasAttribute('hidden')) {
		if (hasState(FAB_STATE)) {
			setState(FAB_STATE, false);
		}
		return;
	}

	const tel = dock.querySelector(DOCK_TEL_SELECTOR);
	if (!tel) {
		return;
	}

	const wasOn = hasState(FAB_STATE);
	if (wasOn) {
		setState(FAB_STATE, false);
	}

	const hit = intersects(fabEl.getBoundingClientRect(), tel.getBoundingClientRect());

	if (hit !== hasState(FAB_STATE)) {
		setState(FAB_STATE, hit);
	}
}

/**
 * Je chatbot FAB v dokumentu? Nalezeny prvek se ulozi, aby se kolize dala
 * MERIT (viz `updateFabCollision()`), ne jen odvodit z jeho existence.
 *
 * @return {boolean} true = FAB nalezen.
 */
function checkFab() {
	fabEl = document.querySelector(FAB_SELECTOR);
	if (fabEl) {
		// Pres rAF agregaci, ne primo (WR-02): `MutationObserver` muze
		// vystrelit nekolikrat za snimek, jak GTM sklada widget po castech.
		fabDirty = true;
		schedule();
	}

	return !!fabEl;
}

/**
 * Zmena rozmeru okna nebo otoceni zarizeni (07-07, bod 5; WR-03 z reviewu;
 * PREPSANO 1.27.13, koordinatoruv nalez -- viz nize).
 *
 * Vlastni posluchac, ne cekani na scroll: uzivatel muze otocit telefon,
 * aniz by se stranka pohnula, a pasmo 441-767 px ma jinou geometrii FABu
 * (20/60 px) nez pasmo do 440 px (12/52 px).
 *
 * `st.scrolled` SE TU UZ NEPREPOCITAVA (1.27.13). Puvodni WR-03 zduvodneni
 * (nize, zamerne PONECHANE jako zaznam problemu, ktery to reilo) zustava
 * pravdive -- otoceni telefonu bez scrollu skutecne muze rozejit prah se
 * skutecnosti. Jenze majitel zmeril PRESNY OPAK skodlivosti: iOS schova/
 * ukaze listu adresy PRI KAZDEM prvnim tapu po scrollu (635 -> 874 px),
 * cimz `resize` vystreli za letu S TAPEM. Kdyby `onViewportChange()`
 * prepocital `st.scrolled` prave tehdy a hodnota by se zmenila, `apply()`
 * by pridala/odebrala `dock-full`/`dock-mini`/`dock-hidden` -- realna
 * mutace primo v hover fazi tapu, presne to, co WebKit bere jako "zmenu
 * obsahu" a klik zahodi ("sjedu niz, vyjede CTA a 2 tapy"). Original WR-03
 * scenar (rotace BEZ scrollu) je vzacny a nemutuje behem tapu; tenhle je
 * castejsi (kazdy prvni tap po libovolnem scrollu) a mutuje presne kdyz
 * nesmi. `st.scrolled` je ted VYHRADNE z `onScroll()` -- po otoceni bez
 * scrollu zustane do dalsiho scrollu ve stavu pred otocenim (degradace
 * "stary, ale stabilni stav" mista "novy, ale nebezpecny prepocet").
 *
 * HEIGHT-ONLY IGNOROVAT I PRO ZBYTEK FUNKCE (kolize FABu). `widthChanged()`
 * chrani cely zbytek -- kdyz se zmenila jen vyska, `fabDirty`/`schedule()`
 * se vubec nevyvolaji, zadna DOM operace v hover fazi tapu neprobehne.
 * Na SKUTECNE zmene sirky (rotace, zmena okna) `updateFabCollision()`
 * porad bezi -- FAB geometrie na sirce zavisi (pasmo 441-767 px vs. do
 * 440 px), takze tuhle cast tapovaci regrese nezpusobuje.
 *
 * @return {void}
 */
function onViewportChange() {
	if (!dockWidthChanged()) {
		return;
	}
	fabDirty = true;
	schedule();
}

/**
 * Sledovani vlozeni chatbot FABu.
 *
 * PROC NESTACI JEDNA KONTROLA PRI INITU: GTM nacita widget asynchronne a
 * ten se do DOM dostane az po dokonceni `fetch` konfigurace -- tedy
 * spolehlive POZDEJI nez tenhle modul. Jednorazova kontrola by kolizi
 * nasla jen nahodou.
 *
 * PROC SE OBSERVER PO NALEZU ODPOJUJE: FAB widget nikdy neodstranuje
 * (zavreni chatu schova jen okno, `widget-terrahunt.js:627`). Observer nad
 * celym podstromem <body> by jinak bezel po zbytek navstevy a reagoval na
 * kazdou zmenu DOM, kterou udela libovolny jiny skript.
 *
 * @return {void}
 */
function watchFab() {
	if (checkFab()) {
		return;
	}

	if (window.MutationObserver) {
		const observer = new window.MutationObserver(function () {
			if (checkFab()) {
				observer.disconnect();
			}
		});
		observer.observe(document.body, { childList: true, subtree: true });

		return;
	}

	// Degradace bez MutationObserver (kontrakt B): dve pozdejsi kontroly.
	window.addEventListener('load', checkFab);
	window.setTimeout(checkFab, FAB_FALLBACK_MS);
}

/**
 * Inicializace modulu.
 *
 * Fail-open: kdyz lista v dokumentu neni (prazdny telefon i CTA v Textech
 * webu), modul se tise vrati a nic dalsiho na nem nestoji.
 *
 * @return {void}
 */
function init() {
	sides = Array.prototype.slice.call(document.querySelectorAll(contracts.SIDE_PANEL));
	dock = document.getElementById(DOCK_ID);
	if (!dock) {
		return;
	}

	window.addEventListener('scroll', onScroll, { passive: true });
	onScroll();

	document.addEventListener('focusin', onFocusIn);
	document.addEventListener('focusout', onFocusOut);

	if (STATIC_TOUCH) {
		// Viz docblok STATIC_TOUCH: zadne IO paticky/formulare, zadny
		// visualViewport, zadny resize -- dock po vyjeti uz stav nemeni.
		watchFab();
		return;
	}

	observeSections();

	if (window.visualViewport) {
		window.visualViewport.addEventListener('resize', measureKeyboard);
		window.visualViewport.addEventListener('scroll', measureKeyboard);
	}

	window.addEventListener('resize', onViewportChange);
	window.addEventListener('orientationchange', onViewportChange);

	watchFab();
}

export { init };
